/*
 * Drops: live countdowns and the switch to the "drop over" state.
 *
 * The end date comes from the product metafield bim.expires_at (unix seconds in data-ends-at).
 * Liquid renders the right state at page build, but storefront pages are cached, so the browser
 * re-checks the clock and flips the closest [data-bim-drop] container to .is-ended when time runs out.
 */

const DAY = 86400;
const pad = (n) => String(n).padStart(2, '0');

function parts(seconds) {
  return {
    d: Math.floor(seconds / DAY),
    h: Math.floor((seconds % DAY) / 3600),
    m: Math.floor((seconds % 3600) / 60),
    s: seconds % 60,
  };
}

// compact: "13 j 04 h" / "04 h 12 min" / "12:09"; full: "13 j 04:12:09"
function format(seconds, mode) {
  const { d, h, m, s } = parts(seconds);
  if (mode === 'full') return `${d > 0 ? `${d} j ` : ''}${pad(h)}:${pad(m)}:${pad(s)}`;
  if (d > 0) return `${d} j ${pad(h)} h`;
  if (h > 0) return `${pad(h)} h ${pad(m)} min`;
  return `${pad(m)}:${pad(s)}`;
}

const timers = new Set();
let interval = null;

function tickAll() {
  for (const timer of timers) timer.tick();
  if (!timers.size) {
    clearInterval(interval);
    interval = null;
  }
}

class BimDropTimer extends HTMLElement {
  connectedCallback() {
    this.endsAt = Number(this.dataset.endsAt);
    if (!Number.isFinite(this.endsAt) || this.endsAt <= 0) return;
    this.output = this.querySelector('[data-bim-time]') ?? this;
    this.urgent = Number(this.dataset.urgentHours || 24) * 3600;
    timers.add(this);
    this.tick();
    interval ??= setInterval(tickAll, 1000);
  }

  disconnectedCallback() {
    timers.delete(this);
  }

  tick() {
    const left = Math.floor(this.endsAt - Date.now() / 1000);
    const scope = this.closest('[data-bim-drop]') ?? this;
    if (left <= 0) {
      timers.delete(this);
      scope.classList.add('is-ended');
      scope.classList.remove('is-urgent');
      return;
    }
    scope.classList.remove('is-ended');
    scope.classList.toggle('is-urgent', left <= this.urgent);
    this.output.textContent = format(left, this.dataset.format);
  }
}

// Date separators in paginated grids: the next page may start with the drop the previous one ended on.
class BimDropDivider extends HTMLElement {
  connectedCallback() {
    const key = this.dataset.key;
    const host = this.closest('li') ?? this;
    let previous = host.previousElementSibling;
    while (previous) {
      const divider = previous.querySelector('bim-drop-divider') ?? (previous.matches('bim-drop-divider') ? previous : null);
      if (divider) {
        if (divider.dataset.key === key) host.remove();
        return;
      }
      previous = previous.previousElementSibling;
    }
  }
}

if (!customElements.get('bim-drop-timer')) customElements.define('bim-drop-timer', BimDropTimer);
if (!customElements.get('bim-drop-divider')) customElements.define('bim-drop-divider', BimDropDivider);
